import { noticeLabelKeys, poiNoticeType } from '@lib/spproject/inspectionStatus';
import { type Inspection } from '@lib/spproject/types';
import { Html } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { type FC, useRef } from 'react';
import { Vector3 } from 'three';
import { Tooltip } from './PoiTooltipStyled';

export const PoiTooltip: FC<{
  hoveredPoi: Inspection.PointOfInterest | undefined;
  t: (
    key: string,
    values?: Array<string | number | boolean> | Record<string, string | number | boolean>
  ) => string;
}> = ({ hoveredPoi, t }) => {
  const { camera } = useThree();
  const offsetRef = useRef(0.05);

  useFrame(() => {
    if (!hoveredPoi) return;
    const poiPosition = new Vector3(
      -hoveredPoi.position.x,
      hoveredPoi.position.y,
      hoveredPoi.position.z
    );
    const distance = camera.position.distanceTo(poiPosition);
    offsetRef.current = Math.max(0.02, Math.min(0.3, distance * 0.05));
  });

  if (!hoveredPoi) return null;

  return (
    <Html
      position={[
        -hoveredPoi.position.x,
        hoveredPoi.position.y + offsetRef.current,
        hoveredPoi.position.z
      ]}
      style={{
        transform: 'translate3d(-50%,-100%,0)',
        pointerEvents: 'none'
      }}
      zIndexRange={[1000, 1000]}
    >
      <Tooltip
        style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.8) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          minWidth: '150px',
          whiteSpace: 'nowrap'
        }}
      >
        <div style={{ marginBottom: '4px' }}>
          <strong style={{ fontSize: '14px', color: '#fff' }}>{hoveredPoi.name}</strong>
        </div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
          {(() => {
            // The point's status, as its marker is coloured — the worst of its steps.
            const noticeType = poiNoticeType(hoveredPoi) ?? 'NotChecked';
            return t('POI_Status_Type', [t(noticeLabelKeys[noticeType])]);
          })()}
        </div>
      </Tooltip>
    </Html>
  );
};